// OrgChart — pan/zoom org-chart canvas for the Structure page. Cards are laid
// out by orgChartLayout, connected by an SVG layer, and navigated via drag,
// wheel-zoom toward the cursor, a minimap, and a zoom toolbar. The canvas
// keeps LTR geometry in both languages (drawn diagram); only card content and
// the overlay position flip under RTL.

import { useEffect, useMemo, useRef } from 'react';
import { OrgUnit } from '@/mocks/structure';
import { FIT_PAD, layoutTree } from './orgChartLayout';
import { useElementSize, usePanZoom } from './useOrgChart';
import OrgChartConnectors from './OrgChartConnectors';
import OrgChartCard from './OrgChartCard';
import OrgChartMinimap from './OrgChartMinimap';
import OrgChartZoom from './OrgChartZoom';

interface OrgChartProps {
  root: OrgUnit;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const OrgChart = ({ root, selectedId, onSelect }: OrgChartProps) => {
  const layout = useMemo(() => layoutTree(root), [root]);
  const [canvasRef, size] = useElementSize();
  const pz = usePanZoom();
  const { t, setT, setBounds, onWheel, fit } = pz;
  const fitted = useRef(false);

  useEffect(() => {
    setBounds(layout.width, layout.height, size.w, size.h);
  }, [layout.width, layout.height, size.w, size.h, setBounds]);

  // Fit once on first non-zero measure; later resizes only update the clamp
  // bounds so they never yank the viewport the user has set.
  useEffect(() => {
    if (!fitted.current && size.w > 0) {
      fit(size.w, size.h, layout.width, layout.height, FIT_PAD);
      fitted.current = true;
    }
  }, [size.w, size.h, layout.width, layout.height, fit]);

  // Native listener: wheel must be non-passive to preventDefault page scroll.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    const handler = (e: WheelEvent) => onWheel(e, el);
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [canvasRef, onWheel]);

  const jump = (cx: number, cy: number) =>
    setT((p) => ({ ...p, x: size.w / 2 - cx * p.s, y: size.h / 2 - cy * p.s }));

  return (
    <div
      ref={canvasRef}
      className='org-chart'
      onMouseDown={pz.onMouseDown}
      onMouseMove={pz.onMouseMove}
      onMouseUp={pz.endDrag}
      onMouseLeave={pz.endDrag}
    >
      <div
        className='org-chart__layer'
        style={{ transform: `translate(${t.x}px, ${t.y}px) scale(${t.s})` }}
      >
        <OrgChartConnectors layout={layout} />
        {layout.nodes.map((rec) => (
          <OrgChartCard
            key={rec.id}
            rec={rec}
            selected={selectedId === rec.id}
            onSelect={onSelect}
          />
        ))}
      </div>
      <div className='org-chart__overlay'>
        <OrgChartMinimap layout={layout} t={t} viewW={size.w} viewH={size.h} onJump={jump} />
        <OrgChartZoom
          scale={t.s}
          onZoom={(f) => pz.zoomBy(f, size.w, size.h)}
          onFit={() => fit(size.w, size.h, layout.width, layout.height, FIT_PAD)}
        />
      </div>
    </div>
  );
};

export default OrgChart;
